import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCheckbox, SpecraIconButton, SpecraInput, SpecraSurface, SpecraTag, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowDown, ArrowUp, MessageCircleQuestion } from 'lucide-react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useChatFeedback, useChatReadOnly } from '../ChatFeedbackContext';
/** The answer line each card's reply starts with; the thread reads it back. */ export const ANSWERS_PREFIX = '[answers]';
/** Answers already sent in this thread, by the asking call: survives a reload. */ const AskAnswersContext = /*#__PURE__*/ createContext(new Map());
export const AskAnswersProvider = AskAnswersContext.Provider;
/** Reads the call id and the answer lines out of an answers message. */ export function parseAnswers(text) {
    if (!text.startsWith(ANSWERS_PREFIX)) return null;
    const [head, ...rest] = text.split('\n');
    const call = /call=(\S+)/.exec(head ?? '')?.[1] ?? '';
    return {
        call,
        lines: rest.filter((l)=>l.trim() !== '')
    };
}
/** Collects answered asks from a thread's user messages. */ export function answeredAsks(texts) {
    const out = new Map();
    for (const t of texts){
        const p = parseAnswers(t);
        if (p?.call) out.set(p.call, p.lines.join('\n'));
    }
    return out;
}
function initial(q) {
    if (q.type === 'multi') return q.recommended ? [
        q.recommended
    ] : [];
    if (q.type === 'rank') return (q.options ?? []).map((o)=>o.label);
    return q.recommended ?? '';
}
function shown(q, v) {
    if (Array.isArray(v)) {
        return q.type === 'rank' ? v.map((x, i)=>`${i + 1}. ${x}`).join(', ') : v.join(', ');
    }
    return q.unit && v ? `${v} ${q.unit}` : v;
}
/** The message the agent reads: one "question: answer" line each. */ export function formatAnswers(call, questions, values, skipAll) {
    const lines = questions.map((q)=>{
        const v = values[q.id];
        const empty = v === undefined || (Array.isArray(v) ? v.length === 0 : v.trim() === '');
        if (skipAll || empty) return `${q.question}: skipped (use your assumption: ${q.assume})`;
        return `${q.question}: ${shown(q, v)}`;
    });
    return [
        `${ANSWERS_PREFIX} call=${call}`,
        ...lines
    ].join('\n');
}
function Choice({ q, value, onChange, multi }) {
    const opts = q.type === 'yesno' ? [
        {
            label: 'Yes'
        },
        {
            label: 'No'
        }
    ] : q.options ?? [];
    const picked = new Set(Array.isArray(value) ? value : value ? [
        value
    ] : []);
    const [other, setOther] = useState('');
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-1.5",
        children: [
            opts.map((o)=>{
                const on = picked.has(o.label);
                return(// specra-allow raw-element: an option card wrapping a native radio; Specra has no radio
                /*#__PURE__*/ _jsxs("label", {
                    className: `flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-2 text-sm transition-colors ${on ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`,
                    children: [
                        multi ? /*#__PURE__*/ _jsx(SpecraCheckbox, {
                            checked: on,
                            onCheckedChange: (c)=>{
                                const next = new Set(picked);
                                if (c) next.add(o.label);
                                else next.delete(o.label);
                                onChange([
                                    ...next
                                ]);
                            },
                            className: "mt-0.5"
                        }) : // specra-allow raw-element: native radio inside the option card (no Specra radio)
                        /*#__PURE__*/ _jsx("input", {
                            type: "radio",
                            name: q.id,
                            checked: on,
                            onChange: ()=>onChange(o.label),
                            className: "mt-1 accent-[var(--primary)]"
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "min-w-0 flex-1",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium",
                                    children: o.label
                                }),
                                o.label === q.recommended ? /*#__PURE__*/ _jsx(SpecraTag, {
                                    tone: "info",
                                    className: "ml-2 align-middle",
                                    children: "Recommended"
                                }) : null,
                                o.detail ? /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    className: "block",
                                    children: o.detail
                                }) : null
                            ]
                        })
                    ]
                }, o.label));
            }),
            q.type === 'choice' ? /*#__PURE__*/ _jsx(SpecraInput, {
                placeholder: "Something else…",
                value: other,
                onChange: (e)=>{
                    setOther(e.target.value);
                    onChange(e.target.value);
                },
                "aria-label": `${q.question}: something else`
            }) : null
        ]
    });
}
function Rank({ value, onChange }) {
    const move = (i, d)=>{
        const next = [
            ...value
        ];
        const j = i + d;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [
            next[j],
            next[i]
        ];
        onChange(next);
    };
    return /*#__PURE__*/ _jsx("ol", {
        className: "flex flex-col gap-1",
        children: value.map((label, i)=>/*#__PURE__*/ _jsxs("li", {
                className: "flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "w-5 text-muted-foreground",
                        children: [
                            i + 1,
                            "."
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "flex-1",
                        children: label
                    }),
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        label: `Move ${label} up`,
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>move(i, -1),
                        children: /*#__PURE__*/ _jsx(ArrowUp, {
                            className: "h-3.5 w-3.5"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        label: `Move ${label} down`,
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>move(i, 1),
                        children: /*#__PURE__*/ _jsx(ArrowDown, {
                            className: "h-3.5 w-3.5"
                        })
                    })
                ]
            }, label))
    });
}
/**
 * The agent's questions: each says why it matters, which answer it
 * recommends and what it will assume if skipped. Answering (or skipping)
 * sends one message the run resumes on; after that the card shows what was
 * answered, from the thread itself, so a reload keeps it answered.
 */ export function AskUserCard({ output, toolCallId }) {
    const questions = output.questions ?? [];
    const send = useChatFeedback();
    const readOnly = useChatReadOnly();
    const answered = useContext(AskAnswersContext);
    const call = toolCallId ?? '';
    const saved = call ? answered.get(call) : undefined;
    const [sent, setSent] = useState(null);
    const [values, setValues] = useState(()=>Object.fromEntries(questions.map((q)=>[
                q.id,
                initial(q)
            ])));
    const done = saved ?? sent;
    const set = (id)=>(v)=>setValues((prev)=>({
                    ...prev,
                    [id]: v
                }));
    const submit = (skipAll)=>{
        const msg = formatAnswers(call, questions, values, skipAll);
        setSent(msg.split('\n').slice(1).join('\n'));
        send(msg);
    };
    const answeredLines = useMemo(()=>done ? done.split('\n') : [], [
        done
    ]);
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        elevation: "xs",
        className: "overflow-hidden",
        "data-testid": Testids.assistant.askCard,
        "data-state": done ? 'answered' : 'open',
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start gap-2 border-b border-border px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(MessageCircleQuestion, {
                        className: "mt-0.5 h-4 w-4 text-primary",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        className: "font-medium",
                        children: output.intro || (questions.length === 1 ? 'One question first' : 'A few questions first')
                    })
                ]
            }),
            done ? /*#__PURE__*/ _jsxs("div", {
                className: "px-4 py-3",
                "data-testid": Testids.assistant.askAnswered,
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        className: "mb-1 block",
                        children: "You answered"
                    }),
                    answeredLines.map((l)=>/*#__PURE__*/ _jsx(SpecraText, {
                            size: "body-sm",
                            className: "block",
                            children: l
                        }, l))
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-col gap-4 px-4 py-3",
                        children: questions.map((q)=>/*#__PURE__*/ _jsxs("fieldset", {
                                className: "flex flex-col gap-1.5",
                                disabled: readOnly,
                                children: [
                                    /*#__PURE__*/ _jsx("legend", {
                                        className: "text-sm font-medium",
                                        children: q.question
                                    }),
                                    q.why ? /*#__PURE__*/ _jsx(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: q.why
                                    }) : null,
                                    q.type === 'choice' || q.type === 'yesno' || q.type === 'multi' ? /*#__PURE__*/ _jsx(Choice, {
                                        q: q,
                                        value: values[q.id] ?? '',
                                        onChange: set(q.id),
                                        multi: q.type === 'multi'
                                    }) : q.type === 'rank' ? /*#__PURE__*/ _jsx(Rank, {
                                        value: values[q.id] ?? [],
                                        onChange: set(q.id)
                                    }) : /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: q.type === 'number' ? 'number' : q.type === 'date' ? 'date' : 'text',
                                                value: values[q.id] ?? '',
                                                onChange: (e)=>set(q.id)(e.target.value),
                                                "aria-label": q.question
                                            }),
                                            q.unit ? /*#__PURE__*/ _jsx(SpecraText, {
                                                size: "meta",
                                                tone: "muted",
                                                children: q.unit
                                            }) : null
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        size: "micro",
                                        tone: "muted",
                                        children: [
                                            "If you skip it: ",
                                            q.assume
                                        ]
                                    })
                                ]
                            }, q.id))
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.assistant.askSubmit,
                                onClick: ()=>submit(false),
                                disabled: readOnly,
                                children: "Send answers"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "outline",
                                "data-testid": Testids.assistant.askSkip,
                                onClick: ()=>submit(true),
                                disabled: readOnly,
                                children: "Skip, use your assumptions"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
